import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { currentWorkspace, listPosts, signMedia, thumbPath, thumbTarget } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { DeletePostDialog } from "../components/DeletePostDialog";
import { useWorkspace } from "../lib/WorkspaceContext";
import { STATUS_CHIP, STATUS_LABEL } from "../lib/postStatus";
import { listPeople } from "../lib/people";
import { PLATFORM_LABEL, type Platform, type Post } from "../lib/types";
import { formatInZone, formatInZoneWithName } from "../lib/timezone";
import { useZone } from "../lib/userSettings";
import { PLATFORM_ICON } from "../components/PlatformIcons";
import { Thumb } from "../components/Thumb";

type Filter = "all" | "published" | "scheduled" | "draft";

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "all", label: "All Posts" },
  { id: "published", label: "Published" },
  { id: "scheduled", label: "Scheduled" },
  { id: "draft", label: "Drafts" },
];

function platformsOf(p: Post): Platform[] {
  const seen = new Set<Platform>();
  for (const d of p.deliveries ?? []) seen.add(d.platform);
  return [...seen];
}

function filterMatch(filter: Filter, p: Post) {
  if (filter === "all") return true;
  if (filter === "published") return p.status === "published" || p.status === "partially_published";
  if (filter === "scheduled") return p.status === "scheduled" || p.status === "publishing";
  return p.status === "draft";
}

function countFor(filter: Filter, posts: Post[]) {
  return posts.filter((p) => filterMatch(filter, p)).length;
}

export function Posts() {
  const zone = useZone();
  // Deleting is its own authority (ADR-0011): admins and owners hold it, a
  // creator does not, and this only decides whether the button is drawn. The
  // database refuses independently.
  const { can, active } = useWorkspace();
  const workspaceId = active?.workspace_id;
  const mayViewMembers = can("member.view");
  const [creators, setCreators] = useState<Record<string, string>>({});
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [compact, setCompact] = useState(false);
  const [thumbs, setThumbs] = useState<Map<string, string>>(new Map());
  // The post the confirmation is open over, or null. Held as the row rather
  // than as an id, because the dialog names what is about to go.
  const [confirming, setConfirming] = useState<Post | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await listPosts(await currentWorkspace());
      setPosts(rows);
      setError(null);
      const seen = new Set<string>();
      const targets = rows
        .map((p) => p.media_assets)
        .filter((a): a is NonNullable<typeof a> => !!a)
        .map(thumbTarget)
        .filter((t): t is NonNullable<typeof t> => t !== null)
        .filter((t) => !seen.has(t.storage_path) && seen.add(t.storage_path));
      setThumbs(await signMedia(targets, { surface: "post" }));
    } catch (e: any) {
      setError(String(e.message ?? e));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    let cancelled = false;
    setCreators({});
    if (workspaceId && mayViewMembers) {
      void listPeople(workspaceId).then((people) => {
        if (!cancelled) {
          setCreators(Object.fromEntries(
            people.filter((person) => person.email).map((person) => [person.user_id, person.email!]),
          ));
        }
      }).catch(() => { /* Author lookup must not hide the posts if it fails. */ });
    }
    return () => { cancelled = true; };
  }, [workspaceId, mayViewMembers]);
  // Deliveries move on the scheduler's clock, so poll while the tab is open —
  // and only while it is actually open (0017-v2 §0A).
  // Paused while a confirmation is open: a dialog naming a post must not have
  // that post replaced underneath the person reading it.
  usePoll(() => void load(), 10_000, { paused: confirming !== null });

  if (error) return <div className="card err">{error}</div>;
  if (!posts) return <p className="muted">Loading…</p>;

  const mayDelete = can("post.delete");
  const normalized = query.trim().toLowerCase();
  const shown = posts.filter((p) => {
    if (!filterMatch(filter, p)) return false;
    if (!normalized) return true;
    const creator = p.created_by ? creators[p.created_by] ?? "" : "";
    return [p.title, p.caption, STATUS_LABEL[p.status], creator]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(normalized));
  });
  const nextAction = filter === "all" ? "scheduled" : "all";
  const guideText = filter === "scheduled"
    ? "Scheduled posts stay together here, with the release date and who prepared them visible before you open the detail."
    : "See what's published, what's scheduled, and what still needs a decision, all in one place.";

  const counts = {
    all: countFor("all", posts),
    published: countFor("published", posts),
    scheduled: countFor("scheduled", posts),
    draft: countFor("draft", posts),
  };

  return (
    <>
      <div className="posts-page">
        <div className="posts-hero">
          <div>
            <h1>Posts</h1>
            <p className="muted">Manage, edit, and track all your published and scheduled posts.</p>
          </div>
          <Link to="/compose" className="posts-create">Create Post</Link>
        </div>

        <div className="posts-filter-row" role="tablist" aria-label="Post status filters">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`posts-filter${filter === item.id ? " active" : ""}`}
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              <span className={`posts-filter-dot ${item.id}`} aria-hidden="true" />
              {item.label}
              <span className="posts-filter-count">{counts[item.id]}</span>
            </button>
          ))}
        </div>

        <div className="posts-toolbar" aria-label="Post tools">
          <label className="posts-search">
            <span aria-hidden="true">⌕</span>
            <span className="visually-hidden">Search posts</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search posts..."
            />
          </label>
          <button type="button" className="posts-tool" aria-label="Filter options">☷</button>
          <button type="button" className="posts-tool" aria-label="Sort posts">↕</button>
          <button
            type="button"
            className={`posts-tool${!compact ? " active" : ""}`}
            aria-label="Comfortable list"
            aria-pressed={!compact}
            onClick={() => setCompact(false)}
          >
            ☰
          </button>
          <button
            type="button"
            className={`posts-tool${compact ? " active" : ""}`}
            aria-label="Compact grid"
            aria-pressed={compact}
            onClick={() => setCompact(true)}
          >
            ⊞
          </button>
        </div>

        <section className="posts-guide" aria-labelledby="posts-guide-title">
          <div className="posts-guide-art" aria-hidden="true">
            <span className="guide-card" />
            <span className="guide-platform instagram">◎</span>
            <span className="guide-platform facebook">f</span>
            <span className="guide-platform youtube">▶</span>
          </div>
          <div>
            <h2 id="posts-guide-title">Track and manage all your posts here</h2>
            <p>{guideText}</p>
            <div className="posts-guide-dots" aria-hidden="true"><span /><span /><span /></div>
          </div>
          <button type="button" className="posts-guide-next" onClick={() => setFilter(nextAction)}>
            Next <span aria-hidden="true">›</span>
          </button>
        </section>

        {posts.length === 0 && (
          <div className="card muted">
            No posts yet. <Link to="/compose">Compose one</Link>.
          </div>
        )}
        {posts.length > 0 && shown.length === 0 && (
          <div className="card muted">No posts match this view.</div>
        )}

        <div className={compact ? "posts-list compact" : "posts-list"}>
      {shown.map((p) => {
        const published = p.deliveries?.filter((d) => d.status === "published").length ?? 0;
        const total = p.deliveries?.length ?? 0;
        const label = p.title || p.caption.slice(0, 60) || "(no caption)";
        const userLabel = p.created_by ? creators[p.created_by] ?? "User unavailable" : "User unavailable";
        const dateLabel = p.scheduled_at
          ? formatInZoneWithName(p.scheduled_at, zone)
          : formatInZone(p.created_at, zone);
        const datePrefix = p.scheduled_at ? "Scheduled" : p.status === "draft" ? "Last edited" : "Posted";
        const platforms = platformsOf(p);
        const asset = p.media_assets;
        const thumbUrl = asset && thumbPath(asset) ? thumbs.get(thumbPath(asset)!) : undefined;

        return (
          // The card is no longer wrapped in the link: a button inside an
          // anchor is invalid, and its click would navigate before it deleted
          // anything. The link stretches over the card instead (`.stretched`),
          // so the whole row still opens the post, and the actions sit above it.
          <div key={p.id} className="card post-row">
            <div className="post-thumb">
              {asset
                ? <Thumb asset={asset} url={thumbUrl} alt="" />
                : <span className="thumb-fallback">{p.kind === "video" ? "▶" : "▧"}</span>}
              {(p.post_media?.length ?? 1) > 1 && (
                <span className="post-media-count">{p.post_media!.length}</span>
              )}
            </div>
            <div className="post-row-main">
              <div className="post-row-top">
                <Link to={`/posts/${p.id}`} className="stretched post-row-title">
                  <strong>{label}</strong>
                </Link>
                <span className={`chip ${STATUS_CHIP[p.status]}`}>{STATUS_LABEL[p.status]}</span>
              </div>
              <p className="post-caption">{p.caption || "No caption yet."}</p>
              <div className="post-meta-line">
                <span className="platform-badges">
                  {platforms.map((platform) => {
                    const Icon = PLATFORM_ICON[platform];
                    return (
                      <span key={platform} title={PLATFORM_LABEL[platform]}>
                        <Icon className="platform-icon" />
                        <span className="visually-hidden">{PLATFORM_LABEL[platform]}</span>
                      </span>
                    );
                  })}
                </span>
                <span>{total} network{total === 1 ? "" : "s"} · {p.kind === "photo" && (p.post_media?.length ?? 1) > 1 ? `${p.post_media!.length} photos` : p.kind}</span>
              </div>
              <div className="post-row-footer">
                <span className="post-row-creator">User: {userLabel}</span>
                <span>{datePrefix} {dateLabel}</span>
              </div>
            </div>
            <div className="post-row-actions">
              {mayDelete && (
                <button
                  className="sm danger row-action"
                  // The status is right there and the title is often
                  // "(no caption)", so the accessible name carries both — six
                  // identical "Delete" buttons in a list is a screen reader
                  // reading out a guess.
                  aria-label={`Delete ${label} (${STATUS_LABEL[p.status]})`}
                  onClick={() => setConfirming(p)}
                >
                  Delete
                </button>
              )}
              <span className="post-menu" aria-hidden="true">⋮</span>
            </div>
            <span className="visually-hidden">{published} of {total} networks published.</span>
          </div>
        );
      })}
        </div>
      </div>

      {confirming && (
        <DeletePostDialog
          postId={confirming.id}
          label={confirming.title || confirming.caption.slice(0, 80) || "(no caption)"}
          published={confirming.deliveries?.filter((d) => d.status === "published").length ?? 0}
          onCancel={() => setConfirming(null)}
          onDeleted={() => {
            // Dropped here rather than waiting for the next poll: the row is
            // gone from the database already, and leaving it on screen for up
            // to ten seconds invites a second delete of a post that no longer
            // exists.
            setPosts((current) => (current ?? []).filter((x) => x.id !== confirming.id));
            setConfirming(null);
          }}
        />
      )}
    </>
  );
}
